import { apiClient } from './client';

// ── Types (mirror myLedger integration DTOs) ──────────────────────────────────
export interface MasterMappingDto {
  id: string;
  masterType: string;
  externalId: string;
  externalCode?: string | null;
  externalName: string;
  externalPayloadJson?: string | null;
  sourceSystem: string;
  mappingStatus: 'PendingMapping' | 'Mapped' | 'Rejected';
  mappedAccountId?: string | null;
  mappedAccountCode?: string | null;
  accountRolesJson?: string | null;
  mappedCostCenterId?: string | null;
  mappedCostCenterCode?: string | null;
  notes?: string | null;
  rejectedReason?: string | null;
  approvedAt?: string | null;
  createdDate: string;
}

export interface MappingCostCenterDto {
  id: string;
  code: string;
  name: string;
}

export interface MasterMappingCounts {
  total: number;
  pending: number;
  mapped: number;
  rejected: number;
}

export interface IntAccountDto {
  id: string;
  code: string;
  name: string;
  accountType: string;
  accountNature: string;
  isPostingAccount: boolean;
  isControlAccount: boolean;
  isActive: boolean;
  parentId?: string | null;
}

export interface IntegrationSettingDto {
  companyId: string;
  integrationEnabled: boolean;
  integrationMode: 'Standalone' | 'Integrated';
  isIntegrated: boolean;
}

export interface MappingPage {
  items: MasterMappingDto[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
}

interface MappingFilter {
  masterType?: string;
  status?: string;
  search?: string;
  pageNumber?: number;
  pageSize?: number;
}

// ── Master mappings ───────────────────────────────────────────────────────────
export const integrationMappingApi = {
  async list(f: MappingFilter): Promise<MappingPage> {
    const p = new URLSearchParams();
    if (f.masterType) p.set('masterType', f.masterType);
    if (f.status)     p.set('status', f.status);
    if (f.search)     p.set('search', f.search);
    p.set('pageNumber', String(f.pageNumber ?? 1));
    p.set('pageSize',   String(f.pageSize ?? 25));
    const res = await apiClient.get(`/master-mappings?${p}`);
    return res.data?.data ?? { items: [], totalCount: 0, pageNumber: 1, pageSize: 25 };
  },

  async counts(masterType?: string): Promise<MasterMappingCounts> {
    const p = masterType ? `?masterType=${encodeURIComponent(masterType)}` : '';
    const res = await apiClient.get(`/master-mappings/counts${p}`);
    return res.data?.data ?? { total: 0, pending: 0, mapped: 0, rejected: 0 };
  },

  async approve(id: string, mappedAccountId: string, notes?: string): Promise<void> {
    await apiClient.post(`/master-mappings/${id}/approve`, { mappedAccountId, notes });
  },

  // Multi-account approve — one GL account per required role (Category, Tax, etc.).
  async approveRoles(id: string, roles: { role: string; accountId: string }[], notes?: string): Promise<void> {
    await apiClient.post(`/master-mappings/${id}/approve-roles`, { roles, notes });
  },

  // Location/Warehouse → cost-centre approve.
  async approveCostCenter(id: string, mappedCostCenterId: string, notes?: string): Promise<void> {
    await apiClient.post(`/master-mappings/${id}/approve-cost-center`, { mappedCostCenterId, notes });
  },

  async reject(id: string, reason: string): Promise<void> {
    await apiClient.post(`/master-mappings/${id}/reject`, { reason });
  },

  // Posting accounts for the mapping account-picker.
  async accounts(): Promise<IntAccountDto[]> {
    const res = await apiClient.get(`/integration/masters/accounts?postingOnly=true`);
    return res.data?.data ?? [];
  },

  // Cost centres for the Location→cost-centre picker.
  async costCenters(): Promise<MappingCostCenterDto[]> {
    const res = await apiClient.get(`/master-mappings/cost-centers`);
    return res.data?.data ?? [];
  },
};

// ── Integration settings ──────────────────────────────────────────────────────
export const integrationSettingsApi = {
  async get(): Promise<IntegrationSettingDto> {
    const res = await apiClient.get('/integration-settings');
    return res.data?.data;
  },
  async update(integrationEnabled: boolean, integrationMode: 'Standalone' | 'Integrated'): Promise<IntegrationSettingDto> {
    const res = await apiClient.put('/integration-settings', { integrationEnabled, integrationMode });
    return res.data?.data;
  },
};
